<template>
  <v-card class="w-100 d-flex flex-column ga-4 pa-8">
    <v-sheet class="header-section">
      <h3 class="title-section">Métricas de tempo de conversão por curso</h3>
      <p class="subtitle-section">
        Exibe as conversões realizadas nos últimos 3 meses considerando as probabilidades atribuídas na última atualização.
      </p>
    </v-sheet>

    <rb-loader v-if="isLoading" v-model="internalData" :contained="true" />

    <FilterComponent
      v-model:view="mode"
      v-model:filters="filtersState"
      :available-ids="availableIdsByCategory"
      :show-chart="false"
      :show-table="false"
      :show-search="true"
      :show-filter="true"
      :show-refresh="true"
      :enableProcessoSeletivo="false"
      :enableProcesso="true"
      :enableModalidade="true"
      :enableNivelEnsino="true"
      :enableCurso="true"
      :enableOfertaCurso="false"
      :enableEtapa="false"
      :enableFaixaConversao="false"
      :show-download="rows && rows.length > 0 && !(showNoDataMessage && isPropDataEmpty && isUsingPropData)"
      :download-disabled="!rows || rows.length === 0"
      @click:download="openDownloadCsvPopup"
      @click:filter="abrirDrawerFiltros()"
      @submit:search="handleSearchSubmit"
      @click:refresh="reloadData()"
      @update:filters="loadData"
    />
    <v-sheet
      v-if="showNoDataMessage && isPropDataEmpty && isUsingPropData"
      class="w-100 d-flex flex-column justify-center align-center"
    >
      <h3 class="without-data">Sem dados disponíveis.</h3>
      <p class="without-data-text">No momento nenhum registro entregou dados necessários</p>
      <p class="without-data-text">para este relatório <strong class="strong-color">nos últimos 3 meses.</strong></p>
    </v-sheet>

    <v-sheet v-if="mode === 'table' && !(showNoDataMessage && isPropDataEmpty && isUsingPropData)" class="w-100 d-flex">
      <TableInsights
        :headers="tableHeaders"
        :items="rows"
        :show-total="true"
        total-title="Total geral / Média ponderada"
        :total-col-span="4"
        :totals="tableTotals"
        v-model:page="page"
        v-model:itemsPerPage="ipp"
        :items-per-page-options="[5,10,25,50]"
        :highlight-index="1"
        @row:click="handleRowClick($event)"
      >
        <template #tooltip-content>
          Exibe a <strong>média (em dias)</strong> que os candidatos levam para converter em matrícula por curso e o <strong>desvio padrão</strong> desse período. O desvio padrão indica a dispersão dos dados: valores baixos sugerem um tempo de conversão constante, enquanto valores altos indicam grande oscilação no tempo de fechamento entre os alunos.
        </template>
      </TableInsights>
    </v-sheet>

    <DetailPopUp
      v-model="detailPopUpVisible"
      :payload="payloadDetails"
      :url="urlDetailPopUp"
      :title-pop-up="detailPopUpTitle"
      subtitle-pop-up="Encontre o contato para realizar análises qualitativas"
      :headers="[
            { title:'Média (dias)', key:'dm_media', align:'start' },
            { title:'Desvio Padrão', key:'dm_desvio_padrao' },
            { title:'Nome', key:'nome' },
            { title:'Curso', key:'curso_nome' },
            { title:'Unidade', key:'nome_unidade' },
            { title:'Modalidade', key:'nome_modalidade' },
            { title:'Nivel  de Ensino', key:'nome_nivelensino' },
          ]"
      :rows-keys="['id', 'dm_media', 'dm_desvio_padrao', 'nome', 'curso_nome', 'nome_unidade', 'nome_modalidade', 'nome_nivelensino']"
      @after-leave="detailPopUpVisible=false"
    />

    <RbPopUp
      v-model="downloadCsvPopupVisible"
      :width="400"
      confirm-text="Download"
      cancel-text="Cancelar"
      confirm-color="primary-60"
      variant-confirm="elevated"
      variant-cancel="outlined"
      :disabled-confirm="!rows || !rows.length"
      @confirm="downloadTableCsv"
    >
      <template #title>Download do relatório (CSV)</template>

      <template #message>
        Realize o download em CSV do widget<br />
        "<strong>Métricas de tempo de conversão por curso</strong>".
      </template>

      <template #confirm-icon>
        <v-icon size="18">mdi-download</v-icon>
      </template>
    </RbPopUp>
  </v-card>
</template>

<script>
import RbLoader from '@/ai_studio/components/common/RbLoader.vue';
import FilterComponent from './FilterComponent.vue';
import TableInsights from './TableInsights.vue';
import DetailPopUp from './detailsPopUp/DetailPopUp.vue';
import { PostWithJson } from '@/ai_studio/http/http.js';
import { downloadCsv } from '@/ai_studio/utils/exportCsv';
import RbPopUp from '@/ai_studio/components/common/RBPopup.vue';
import { calculateAvailableIds } from '@/ai_studio/utils/entityHelper';

export default {
  name: 'ConversionbyCourseOffer',
  components: {
    RbLoader,
    FilterComponent,
    TableInsights,
    DetailPopUp,
    RbPopUp
  },

  props: {
    data: { type: [Array, Object], default: () => [] },
    filters: { type: Array, default: () => []}
  },

  data() {
    return {
      mode: 'table',
      rows: [],
      tableTotals: {},
      page: 1,
      ipp: 5,
      search: '',
      internalData: this.data,
      filtersState: this.filters || {},
      originalAvailableIds: null,
      isLoading: false,
      detailPopUpTitle: '',
      urlDetailPopUp: '',
      idCurso: null,
      payloadDetails: {},
      detailPopUpVisible: false,
      showNoDataMessage: false,
      downloadCsvPopupVisible: false,
    };
  },

  computed: {
    isPropDataEmpty() {
      const d = this.data;
      const dataset = Array.isArray(d)
        ? d
        : Array.isArray(d?.dados)
          ? d.dados
          : [];
      return dataset.length === 0;
    },

    isUsingPropData() {
      return this.internalData === this.data;
    },

    dataset() {
      const d = this.internalData;
      return Array.isArray(d)
        ? d
        : Array.isArray(d?.dados)
          ? d.dados
          : [];
    },

    availableIdsByCategory() {
      return this.originalAvailableIds || calculateAvailableIds(this.dataset);
    },
    tableHeaders() {
      return [
        { title:'Processo', key:'processo_nome' },
        { title:'Curso', key:'curso_nome' },
        { title:'Unidade', key:'nome_unidade' },
        { title:'Modalidade', key:'nome_modalidade' },
        { title:'Nível de Ensino', key:'nome_nivelensino' },
        { title:'Inscrições', key:'inscricoes' },
        { title:'Média (dias)', key:'dm_media' },
        { title:'Desvio Padrão', key:'dm_desvio_padrao' },
      ]
    },
  },

  watch: {
    data: {
      immediate: true,
      deep: true,
      handler(newData) {
        this.internalData = newData;
        this.processData(newData);

        if (newData && !this.originalAvailableIds) {
          const ids = calculateAvailableIds(newData);
          if (Object.keys(ids).length > 0) {
            this.originalAvailableIds = ids;
          }
        }
      },
    },
    filters: {
      deep: true,
      immediate: true,
      handler(v) {
        this.filtersState = v || {}
      }
    },
    internalData: {
      deep: true,
      handler(newData) {
        this.processData(newData);
      }
    }
  },

  methods: {
    openDownloadCsvPopup() {
      if (!this.rows || !this.rows.length) return
      this.downloadCsvPopupVisible = true
    },

    downloadTableCsv() {
      const fields = this.tableHeaders.map(header => header.title)

      const data = (this.rows || []).map(row => ([
        row.processo_nome ?? '',
        row.curso_nome ?? '',
        row.nome_unidade ?? '',
        row.nome_modalidade ?? '',
        row.nome_nivelensino ?? '',
        Number(row.inscricoes || 0).toLocaleString('pt-BR'),
        Number(row.dm_media || 0).toFixed(1).replace('.', ','),
        Number(row.dm_desvio_padrao || 0).toFixed(1).replace('.', ','),
      ]))

      if (this.tableTotals && Object.keys(this.tableTotals).length) {
        const totalInscricoes = Number(this.tableTotals.inscricoes || 0).toLocaleString('pt-BR')

        const media = String(this.tableTotals.dm_media ?? '')
        const desvio = String(this.tableTotals.dm_desvio_padrao ?? '')

        data.push([
          'Resumo dos dados',
          '',
          '',
          '',
          totalInscricoes,
          media,
          desvio
        ])
      }

      const today = new Date().toISOString().slice(0, 10)
      downloadCsv({
        filename: `tempo_conversao_por_curso_${today}.csv`,
        fields,
        data,
        delimiter: ';'
      })
    },

    abrirDrawerFiltros() {},

    handleRowClick(row) {
      this.detailPopUpTitle = `${row.curso_nome}`
      this.detailPopUpVisible = true
      this.urlDetailPopUp = 'insights/getReportForecastByTimeCourse'
      this.payloadDetails = {
        course: row.curso_id,
        education_level: row.nivel_ensino_id,
        unit: row.unidade_id,
        modality: row.modalidade_id,
        process: row.processo_id
      }
    },
    
    processData(data) {
      const dataset = Array.isArray(data)
          ? data
          : Array.isArray(data?.dados)
            ? data.dados
            : [];

      this.showNoDataMessage = dataset.length === 0;

      if (dataset.length === 0) {
        this.rows = [];
        this.tableTotals = {};
        return;
      }

      this.populateTable(dataset);
    },
    
    populateTable(data) {
      const filtered = data.filter((item) => {
        const media = Number(item.dm_media);
        const dp = Number(item.dm_desvio_padrao);
        return !(dp && media > dp * 3);
      });

      this.rows = filtered.map((item) => ({
        processo_nome: item.nome_processo,
        curso_nome: item.curso_nome,
        curso_id: item.curso,
        nome_unidade: item.nome_unidade,
        unidade_id: item.unidade,
        nome_modalidade: item.nome_modalidade,
        modalidade_id: item.modalidade,
        nome_nivelensino: item.nome_nivelensino,
        nivel_ensino_id: item.nivelensino,
        processo_id: item.processo,
        inscricoes: Number(item.inscricoes),
        dm_media: Number(item.dm_media),
        dm_desvio_padrao: Number(item.dm_desvio_padrao),
      }));

      this.calculateTotals(filtered);
    },

    calculateTotals(data) {
      const parseNumberBR = (value) => {
        if (value === null || value === undefined) return 0
        let s = String(value).trim()

        s = s.replace(/[^\d,.-]/g, '')

        if (s.includes(',') && s.includes('.')) {
          s = s.replace(/\./g, '').replace(',', '.')
        } else if (s.includes(',')) {
          s = s.replace(',', '.')
        }

        const n = parseFloat(s)
        return Number.isFinite(n) ? n : 0
      }

      const parseIntSafe = (value) => {
        if (value === null || value === undefined) return 0
        const s = String(value).replace(/[^\d-]/g, '')
        const n = parseInt(s, 10)
        return Number.isFinite(n) ? n : 0
      }

      const items = Array.isArray(data) ? data : []

      let totalInscricoes = 0

      let somaPonderadaMedia = 0
      let somaPonderadaDesvio = 0

      for (const cur of items) {
        const peso = parseIntSafe(cur.inscricoes)
        if (peso <= 0) continue

        const media = parseNumberBR(cur.dm_media)
        const desvio = parseNumberBR(cur.dm_desvio_padrao)

        totalInscricoes += peso
        somaPonderadaMedia += media * peso
        somaPonderadaDesvio += desvio * peso
      }

      const mediaPonderada = totalInscricoes > 0
        ? (somaPonderadaMedia / totalInscricoes)
        : 0

      const desvioPonderado = totalInscricoes > 0
        ? (somaPonderadaDesvio / totalInscricoes)
        : 0

      this.tableTotals = {
        inscricoes: totalInscricoes,
        dm_media: `${mediaPonderada.toFixed(1)} dias`,
        dm_desvio_padrao: `${desvioPonderado.toFixed(1)} dias`,
      }
    },


    async loadData(newFilters) {
      this.isLoading = true;

      if (newFilters) {
        this.filtersState = newFilters;
      }
      const body = {
        education_level: this.filtersState.nivelEnsino,
        modality:        this.filtersState.modalidade,
        process:         this.filtersState.processo,
        course:          this.filtersState.curso,
      };

      const url = 'insights/getReportTimeByCourse';

      try {
        const responseData = await PostWithJson(url, JSON.stringify(body));
        this.internalData = responseData;
        if (!this.originalAvailableIds) {
          this.originalAvailableIds = calculateAvailableIds(responseData);
        }
      } catch (error) {
        console.error('Erro ao carregar dados de conversao por curso:', error);
      } finally {
        this.isLoading = false;
      }
    },

    async handleSearchSubmit(searchValue) {
      const data = { search: searchValue };
      const url = `insights/getReportTimeByCourse`;

      try {
        const newData = await PostWithJson(url, JSON.stringify(data));

        if (newData) {
          this.internalData = newData;
          this.page = 1;
          if (!this.originalAvailableIds) {
            this.originalAvailableIds = calculateAvailableIds(newData);
          }
        }
      } catch (error) {
        console.error("Erro ao buscar dados de tempo de conversão por curso:", error);
      }
    },

    async reloadData() {
      this.originalAvailableIds = null;
      await this.loadData(this.filtersState);
    },
  },
};
</script>

<style scoped>
.header-section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: start;
  gap: 8px;
  align-self: stretch;
}
.title-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.15px;
}
.subtitle-section {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.25px;
}

.strong-color {
  color: #004F4F;
}

.without-data {
  color: var(--Text-Neutral-Primary, #191C1C);
  font-family: var(--Typescale-Title-Medium-Font-family, Roboto);
  font-size: var(--Typescale-Title-Medium-Font-size, 16px);
  font-style: normal;
  font-weight: 500;
  line-height: var(--Typescale-Title-Medium-Line-height, 24px);
  letter-spacing: var(--Typescale-Title-Medium-Tracking, 0.15px);
}

.without-data-text {
  color: var(--Text-Neutral-Secondary, #2E3131);
  text-align: center;
  font-family: var(--Typescale-Body-Medium-Font-family, Roboto);
  font-size: var(--Typescale-Body-Medium-Font-size, 14px);
  font-style: normal;
  font-weight: 400;
  line-height: var(--Typescale-Body-Medium-Line-height, 20px);
  letter-spacing: var(--Typescale-Body-Medium-Tracking, 0.25px);
}
</style>